import React, { useState, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScheduleManager = ({ timeSlots, onCreateSlot, onRefresh, t }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [formData, setFormData] = useState({
    date: '',
    start_time: '',
    end_time: '',
    max_clients: 6,
    recurring_weeks: 0
  });
  const [selectedSlots, setSelectedSlots] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [viewMode, setViewMode] = useState('week'); // 'week', 'month', 'list'
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Monday
    return new Date(today.setDate(diff));
  });

  // Get week dates
  const weekDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [currentWeekStart]);

  // Group slots by date
  const slotsByDate = useMemo(() => {
    const grouped = {};
    timeSlots.forEach(slot => {
      const dateKey = slot.date;
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(slot);
    });
    // Sort slots by start_time within each date
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => a.start_time.localeCompare(b.start_time));
    });
    return grouped;
  }, [timeSlots]);

  // Filter slots for current week view
  const currentWeekSlots = useMemo(() => {
    const startStr = currentWeekStart.toISOString().split('T')[0];
    const endDate = new Date(currentWeekStart);
    endDate.setDate(endDate.getDate() + 6);
    const endStr = endDate.toISOString().split('T')[0];
    
    return timeSlots.filter(slot => slot.date >= startStr && slot.date <= endStr);
  }, [timeSlots, currentWeekStart]);

  // Stats
  const stats = useMemo(() => {
    const total = timeSlots.length;
    const thisWeek = currentWeekSlots.length;
    const available = timeSlots.filter(s => s.is_available).length;
    return { total, thisWeek, available };
  }, [timeSlots, currentWeekSlots]);

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    setCurrentWeekStart(new Date(today.setDate(diff)));
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0];
  };

  const toggleSlotSelection = (slotId) => {
    const newSelected = new Set(selectedSlots);
    if (newSelected.has(slotId)) {
      newSelected.delete(slotId);
    } else {
      newSelected.add(slotId);
    }
    setSelectedSlots(newSelected);
  };

  const selectAllVisible = () => {
    if (selectedSlots.size === currentWeekSlots.length) {
      setSelectedSlots(new Set());
    } else {
      setSelectedSlots(new Set(currentWeekSlots.map(s => s.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSlots.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedSlots.size} time slots?`)) return;
    
    try {
      for (const slotId of selectedSlots) {
        await axios.delete(`${API}/timeslots/${slotId}`);
      }
      setSelectedSlots(new Set());
      setBulkMode(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting time slots:', error);
      alert('❌ Error deleting some time slots');
    }
  };

  const handleEditSlot = async (updatedData) => {
    try {
      await axios.put(`${API}/timeslots/${editingSlot.id}`, updatedData);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating time slot:', error);
      alert('❌ Error updating time slot: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (window.confirm('Are you sure you want to delete this time slot?')) {
      try {
        await axios.delete(`${API}/timeslots/${slotId}`);
        if (onRefresh) onRefresh();
      } catch (error) {
        console.error('Error deleting time slot:', error);
        alert('❌ Error deleting time slot: ' + (error.response?.data?.detail || 'Unknown error'));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSlot) {
        await handleEditSlot(formData);
      } else {
        await onCreateSlot(formData);
      }
      setFormData({ date: '', start_time: '', end_time: '', max_clients: 6, recurring_weeks: 0 });
      setShowForm(false);
      setEditingSlot(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error saving time slot:', error);
      alert('❌ Error saving time slot');
    }
  };

  const openEditForm = (slot) => {
    setEditingSlot(slot);
    setFormData({
      date: slot.date,
      start_time: slot.start_time,
      end_time: slot.end_time,
      max_clients: slot.max_clients,
      recurring_weeks: 0
    });
    setShowForm(true);
  };

  const openCreateFormForDate = (date) => {
    setEditingSlot(null);
    setFormData({
      date: formatDateKey(date),
      start_time: '',
      end_time: '',
      max_clients: 6,
      recurring_weeks: 0
    });
    setShowForm(true);
  };

  return (
    <div>
      {/* Header with Stats */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-bold text-white">{t('schedule')} Management</h2>
          <div className="flex gap-4 mt-1 text-sm">
            <span className="text-blue-300">📊 Total: {stats.total}</span>
            <span className="text-green-300">✅ Available: {stats.available}</span>
            <span className="text-purple-300">📅 This week: {stats.thisWeek}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setBulkMode(!bulkMode);
              if (bulkMode) setSelectedSlots(new Set());
            }}
            className={`btn-secondary text-sm ${bulkMode ? 'bg-purple-500/30' : ''}`}
          >
            {bulkMode ? '✕ Exit' : '☑️ Bulk'}
          </button>
          <button
            onClick={() => {
              setShowForm(!showForm);
              if (showForm) {
                setEditingSlot(null);
                setFormData({ date: '', start_time: '', end_time: '', max_clients: 6, recurring_weeks: 0 });
              }
            }}
            className="btn-primary text-sm"
          >
            {showForm ? t('cancel') : '+ New Slot'}
          </button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {bulkMode && (
        <div className="glass-card p-3 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={selectAllVisible} className="btn-secondary text-xs">
              {selectedSlots.size === currentWeekSlots.length ? '☐ Deselect' : '☑️ Select Week'}
            </button>
            <span className="text-blue-200 text-sm">{selectedSlots.size} selected</span>
          </div>
          {selectedSlots.size > 0 && (
            <button onClick={handleBulkDelete} className="bg-red-500/20 text-red-300 hover:bg-red-500/30 px-3 py-1 rounded text-sm">
              🗑️ Delete ({selectedSlots.size})
            </button>
          )}
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div className="glass-card p-4 mb-4">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingSlot ? 'Edit Time Slot' : 'Create New Time Slot'}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-blue-200 text-xs font-medium mb-1">Date</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="input-field text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-blue-200 text-xs font-medium mb-1">Start</label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                className="input-field text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-blue-200 text-xs font-medium mb-1">End</label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                className="input-field text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-blue-200 text-xs font-medium mb-1">Max Clients</label>
              <input
                type="number"
                value={formData.max_clients}
                onChange={(e) => setFormData({...formData, max_clients: parseInt(e.target.value)})}
                className="input-field text-sm"
                min="1"
                required
              />
            </div>
            
            {/* Recurring option - only for new slots */}
            {!editingSlot && (
              <div className="col-span-2">
                <label className="block text-blue-200 text-xs font-medium mb-1">🔄 Repeat Weekly</label>
                <select
                  value={formData.recurring_weeks}
                  onChange={(e) => setFormData({...formData, recurring_weeks: parseInt(e.target.value)})}
                  className="input-field text-sm"
                >
                  <option value={0}>No repeat</option>
                  <option value={4}>4 weeks</option>
                  <option value={12}>12 weeks (3 months)</option>
                  <option value={24}>24 weeks (6 months)</option>
                  <option value={52}>52 weeks (1 year)</option>
                </select>
              </div>
            )}
            
            <div className={editingSlot ? "col-span-2" : ""}>
              <button type="submit" className="btn-primary text-sm w-full mt-4">
                {editingSlot ? 'Update' : formData.recurring_weeks > 0 ? `Create ${formData.recurring_weeks + 1} Slots` : 'Create Slot'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-4 glass-card p-3">
        <button onClick={() => navigateWeek(-1)} className="btn-secondary text-sm">
          ← Previous Week
        </button>
        <div className="flex items-center gap-3">
          <button onClick={goToToday} className="text-blue-300 hover:text-white text-sm">
            Today
          </button>
          <span className="text-white font-semibold">
            {formatDate(weekDates[0])} - {formatDate(weekDates[6])}
          </span>
        </div>
        <button onClick={() => navigateWeek(1)} className="btn-secondary text-sm">
          Next Week →
        </button>
      </div>

      {/* Weekly Calendar View */}
      <div className="grid grid-cols-7 gap-2">
        {weekDates.map((date, idx) => {
          const dateKey = formatDateKey(date);
          const daySlots = slotsByDate[dateKey] || [];
          const isToday = formatDateKey(new Date()) === dateKey;
          const isPast = date < new Date().setHours(0,0,0,0);
          
          return (
            <div 
              key={idx} 
              className={`glass-card p-2 min-h-[200px] ${isToday ? 'ring-2 ring-blue-400' : ''} ${isPast ? 'opacity-60' : ''}`}
            >
              {/* Day Header */}
              <div className="text-center mb-2 pb-2 border-b border-blue-400/20">
                <div className="text-xs text-blue-300">
                  {date.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div className={`text-lg font-bold ${isToday ? 'text-blue-400' : 'text-white'}`}>
                  {date.getDate()}
                </div>
                <div className="text-xs text-blue-400">
                  {daySlots.length > 0 && `${daySlots.length} slots`}
                </div>
              </div>
              
              {/* Slots for this day */}
              <div className="space-y-1 max-h-[300px] overflow-y-auto">
                {daySlots.map(slot => (
                  <div 
                    key={slot.id}
                    className={`p-2 rounded text-xs cursor-pointer transition-all ${
                      selectedSlots.has(slot.id) 
                        ? 'bg-purple-500/40 ring-1 ring-purple-400' 
                        : slot.is_available 
                          ? 'bg-green-500/20 hover:bg-green-500/30' 
                          : 'bg-red-500/20 hover:bg-red-500/30'
                    }`}
                    onClick={() => bulkMode ? toggleSlotSelection(slot.id) : openEditForm(slot)}
                  >
                    {bulkMode && (
                      <input 
                        type="checkbox" 
                        checked={selectedSlots.has(slot.id)} 
                        onChange={() => {}}
                        className="mr-1"
                      />
                    )}
                    <div className="font-semibold text-white">
                      {slot.start_time} - {slot.end_time}
                    </div>
                    <div className="text-blue-300">
                      {slot.current_clients}/{slot.max_clients} clients
                    </div>
                    {!bulkMode && (
                      <div className="flex gap-1 mt-1">
                        <button 
                          onClick={(e) => { e.stopPropagation(); openEditForm(slot); }}
                          className="text-yellow-400 hover:text-yellow-300"
                        >
                          ✏️
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleDeleteSlot(slot.id); }}
                          className="text-red-400 hover:text-red-300"
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Add slot button */}
              {!isPast && !bulkMode && (
                <button 
                  onClick={() => openCreateFormForDate(date)}
                  className="w-full mt-2 p-1 text-xs text-blue-300 hover:text-white hover:bg-blue-500/20 rounded"
                >
                  + Add
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Stats Footer */}
      {currentWeekSlots.length === 0 && (
        <div className="text-center text-blue-300 py-8">
          No time slots scheduled for this week
        </div>
      )}
    </div>
  );
};

export default ScheduleManager;
